import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

interface Project {
  id: string;
  title: string;
  description: string | null;
  image_url: string;
}

interface ProjectLightboxProps {
  projects: Project[];
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ZOOM_FACTOR = 2.5;
const LENS_SIZE = 180;

const ProjectLightbox = ({ projects, initialIndex, open, onOpenChange }: ProjectLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [lens, setLens] = useState<{ x: number; y: number; bgX: number; bgY: number; visible: boolean; naturalW: number; naturalH: number }>({
    x: 0, y: 0, bgX: 0, bgY: 0, visible: false, naturalW: 0, naturalH: 0,
  });
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    setLens(l => ({ ...l, visible: false }));
  }, [currentIndex]);

  const goNext = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % projects.length);
  }, [projects.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + projects.length) % projects.length);
  }, [projects.length]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") goNext();
      else if (e.key === "ArrowLeft") goPrev();
      else if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, goNext, goPrev, onOpenChange]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLImageElement>) => {
    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const percX = x / rect.width;
    const percY = y / rect.height;
    const bgW = rect.width * ZOOM_FACTOR;
    const bgH = rect.height * ZOOM_FACTOR;
    const bgX = -(percX * bgW - LENS_SIZE / 2);
    const bgY = -(percY * bgH - LENS_SIZE / 2);
    setLens({ x: e.clientX, y: e.clientY, bgX, bgY, visible: true, naturalW: bgW, naturalH: bgH });
  }, []);

  if (!projects.length) return null;
  const project = projects[currentIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto h-auto p-0 border-none bg-transparent shadow-none [&>button]:hidden">
        <div className="relative flex items-center justify-center w-full h-full">
          {/* Close */}
          <button
            onClick={() => onOpenChange(false)}
            className="absolute top-3 right-3 z-50 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center text-foreground hover:bg-background transition-colors"
          >
            <X size={20} />
          </button>

          {/* Prev */}
          {projects.length > 1 && (
            <button
              onClick={goPrev}
              className="absolute left-2 md:left-4 z-50 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center text-foreground hover:bg-background transition-colors"
            >
              <ChevronLeft size={22} />
            </button>
          )}

          {/* Image + info */}
          <div className="flex flex-col items-center max-w-[90vw] max-h-[90vh]">
            <AnimatePresence mode="wait">
              <motion.div
                key={project.id}
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.25 }}
                className="relative"
              >
                <img
                  ref={imgRef}
                  src={project.image_url}
                  alt={project.title}
                  className="max-w-[90vw] max-h-[78vh] object-contain rounded-sm cursor-crosshair"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={() => setLens(l => ({ ...l, visible: false }))}
                />
              </motion.div>
            </AnimatePresence>

            <div className="mt-3 text-center px-4 pb-2">
              <h3 className="font-heading text-sm md:text-base font-semibold text-white uppercase tracking-wide drop-shadow-lg">
                {project.title}
              </h3>
              {project.description && (
                <p className="text-white/70 font-body text-xs md:text-sm mt-1 drop-shadow">
                  {project.description}
                </p>
              )}
              {projects.length > 1 && (
                <p className="text-white/40 font-body text-xs mt-2">
                  {currentIndex + 1} / {projects.length}
                </p>
              )}
            </div>
          </div>

          {/* Next */}
          {projects.length > 1 && (
            <button
              onClick={goNext}
              className="absolute right-2 md:right-4 z-50 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center text-foreground hover:bg-background transition-colors"
            >
              <ChevronRight size={22} />
            </button>
          )}
        </div>

      </DialogContent>

      {/* Magnifying lens - portaled to body to avoid transform offsets */}
      {lens.visible && createPortal(
        <div
          className="fixed pointer-events-none rounded-full border-2 border-white/40 shadow-2xl"
          style={{
            zIndex: 9999,
            width: LENS_SIZE,
            height: LENS_SIZE,
            left: lens.x - LENS_SIZE / 2,
            top: lens.y - LENS_SIZE / 2,
            backgroundImage: `url(${project.image_url})`,
            backgroundSize: `${lens.naturalW}px ${lens.naturalH}px`,
            backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
            backgroundRepeat: "no-repeat",
          }}
        />,
        document.body
      )}
    </Dialog>
  );
};

export default ProjectLightbox;
